import React from 'react';

// Library
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './redux/store';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';

// Components
import App from './App.jsx';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

// MUI Components
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Fade from '@mui/material/Fade';

// Fonts
import '@fontsource/inter'; // Defaults to weight 400
import '@fontsource/inter/300.css'; // Specify weight
import '@fontsource/inter/400.css'; // Specify weight
import '@fontsource/inter/500.css'; // Specify weight
import '@fontsource/inter/600.css'; // Specify weight
import '@fontsource/inter/700.css'; // Specify weight

import './index.scss';
import './olc.scss';

Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api.openletterconnect\.com\/api/],
  profilesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1536,
    },
  },
  palette: {
    background: {
      default: '#f9fafb',
    },
  },
  typography: {
    fontFamily: 'Inter', // Set Inter as the default font
  },
  components: {
    // Closing a modal should clear the modal interaction without re-focusing
    // the control that opened it.
    MuiModal: {
      defaultProps: {
        disableRestoreFocus: true,
      },
    },
    // Use the same simple fade as the Help/Profile menus for action menus and
    // every Select-based single or multi-select dropdown.
    MuiMenu: {
      defaultProps: {
        TransitionComponent: Fade,
        transitionDuration: {
          enter: 300,
          exit: 200,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: OlcDropdownChevron,
      },
    },
  },
});

const saveApiKeyFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const apiKey = urlParams.get('apiKey');

  if (apiKey) {
    localStorage.setItem('apiKey', apiKey);

    const newUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
  }
};

saveApiKeyFromUrl();

// A handoff login replaces any existing session. Clear it before App mounts so stale
// tokens don't trigger authenticated calls (and a 401 redirect) while the code is exchanged.
if (window.location.pathname === '/auth/callback') {
  localStorage.clear();
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <Provider store={store}>
    <React.StrictMode>
      <Router>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </Router>
    </React.StrictMode>
  </Provider>
);
